// Pure helpers for the Print Labels page: copy caps and page layout (POS-I442, POS-I443).

export const MAX_COPIES_PER_LABEL = 500;
export const MAX_TOTAL_LABELS = 2000;

/** Clamp a requested copies value for one product, given the labels already queued for the others. */
export function clampCopies(
  requested: number,
  othersTotal: number,
): { value: number; message: string | null } {
  let value = Math.floor(requested);
  let message: string | null = null;
  if (value > MAX_COPIES_PER_LABEL) {
    value = MAX_COPIES_PER_LABEL;
    message = `Copies per label is limited to ${MAX_COPIES_PER_LABEL}. Set to ${MAX_COPIES_PER_LABEL}.`;
  }
  const room = Math.max(0, MAX_TOTAL_LABELS - othersTotal);
  if (value > room) {
    value = room;
    message = `A print run is limited to ${MAX_TOTAL_LABELS} labels in total. Copies set to ${room}.`;
  }
  return { value, message };
}

export interface PagePreset {
  id: string;
  label: string;
  /** 'label' = page equals the chosen label size (roll printer, one label per page). */
  kind: 'roll' | 'sheet';
  pageWidthMm?: number;
  pageHeightMm?: number;
  /** Sheet presets with fixed cells (label sheet stock). */
  cols?: number;
  rows?: number;
  cellWidthMm?: number;
  cellHeightMm?: number;
  marginMm: number;
  gapMm: number;
}

export const PAGE_PRESETS: PagePreset[] = [
  { id: 'roll', label: 'Roll printer (page = label size)', kind: 'roll', marginMm: 0, gapMm: 0 },
  { id: 'a4', label: 'A4 (210×297mm)', kind: 'sheet', pageWidthMm: 210, pageHeightMm: 297, marginMm: 8, gapMm: 2 },
  { id: 'letter', label: 'Letter (216×279mm)', kind: 'sheet', pageWidthMm: 216, pageHeightMm: 279, marginMm: 8, gapMm: 2 },
  { id: 'a4-24', label: 'A4 label sheet 24-up (3×8, 70×37mm)', kind: 'sheet', pageWidthMm: 210, pageHeightMm: 297, cols: 3, rows: 8, cellWidthMm: 70, cellHeightMm: 37, marginMm: 0, gapMm: 0 },
  { id: 'a4-65', label: 'A4 label sheet 65-up (5×13, 38×21mm)', kind: 'sheet', pageWidthMm: 210, pageHeightMm: 297, cols: 5, rows: 13, cellWidthMm: 38, cellHeightMm: 21, marginMm: 0, gapMm: 0 },
  { id: 'letter-30', label: 'Letter label sheet 30-up (3×10, 66×25mm)', kind: 'sheet', pageWidthMm: 216, pageHeightMm: 279, cols: 3, rows: 10, cellWidthMm: 66, cellHeightMm: 25, marginMm: 0, gapMm: 0 },
];

export interface SheetLayout {
  pageCss: string;       // value for @page size
  cols: number;
  rows: number;
  perPage: number;
  cellWidthMm: number;
  cellHeightMm: number;
  marginMm: number;
  gapMm: number;
  scale: number;         // label scale to fit its cell
  pageWidthMm: number;
  pageHeightMm: number;
}

export function computeLayout(preset: PagePreset, labelWMm: number, labelHMm: number): SheetLayout {
  if (preset.kind === 'roll') {
    return {
      pageCss: `${labelWMm}mm ${labelHMm}mm`, cols: 1, rows: 1, perPage: 1,
      cellWidthMm: labelWMm, cellHeightMm: labelHMm, marginMm: 0, gapMm: 0, scale: 1,
      pageWidthMm: labelWMm, pageHeightMm: labelHMm,
    };
  }
  const pw = preset.pageWidthMm!;
  const ph = preset.pageHeightMm!;
  const m = preset.marginMm;
  const g = preset.gapMm;
  const cw = preset.cellWidthMm ?? labelWMm;
  const ch = preset.cellHeightMm ?? labelHMm;
  const cols = preset.cols ?? Math.max(1, Math.floor((pw - 2 * m + g) / (cw + g)));
  const rows = preset.rows ?? Math.max(1, Math.floor((ph - 2 * m + g) / (ch + g)));
  const scale = Math.min(1, cw / labelWMm, ch / labelHMm);
  return {
    pageCss: `${pw}mm ${ph}mm`, cols, rows, perPage: cols * rows,
    cellWidthMm: cw, cellHeightMm: ch, marginMm: m, gapMm: g, scale,
    pageWidthMm: pw, pageHeightMm: ph,
  };
}

export function pageCount(totalLabels: number, perPage: number): number {
  return perPage > 0 ? Math.ceil(totalLabels / perPage) : 0;
}
